// =============================================================================
// OBIEG DOKUMENTÓW — ODCZYT LISTY Z BAZY (UD-12a)
//
// Ten sam podział, co w `devices/receptorCoreApi.ts`: ODCZYT wprost z bazy,
// kluczem zalogowanego człowieka. Ekran niczego nie zapisuje, więc usługa nie
// jest tu potrzebna — i odczyt działa także wtedy, gdy `VITE_RECEPTOR_CORE_URL`
// jest puste.
//
//   * `commerce_documents` czyta `authenticated` WYŁĄCZNIE przez grant na
//     NAZWANE kolumny (786) i pod polityką `is_super_admin() OR
//     is_org_writer(organization_id)`. Lista kolumn jest więc zamknięta
//     (`KOLUMNY_DOKUMENTU_OBIEGU`), a `select('*')` byłby błędem 42501.
//   * Organizacja jest w filtrze JAWNIE: super-admin widzi wszystkie
//     organizacje, więc sama polityka nie zawęża.
//   * Błąd albo brak danych to WYJĄTEK, nigdy pusta lista — pusta lista znaczy
//     dla człowieka „nie ma dokumentów”.
//   * Lista i liczba przy chipie biorą TE SAME filtry rodzaju, zakładu
//     i numeru — liczba, która liczy coś innego niż lista pod nią, kłamie.
// =============================================================================

import type { RodzajDokumentuMagazynowego } from '@insolitum/types';

import { supabase } from '../../../lib/supabase';
import { ReceptorCoreError } from '../devices/receptorCoreApi';

import {
  FILTR_WYMAGA_DZIALANIA,
  KOLUMNY_DOKUMENTU_OBIEGU,
  ROZMIAR_STRONY,
  ucieknijLike,
  type DokumentObiegu,
  type NajnowszyDokument,
} from './dokumentyObiegu';

/** Filtry zapytania — okres już zwalidowany, `doWylacznie` = dzień PO ostatnim. */
export interface FiltryZapytaniaDokumentow {
  od: string;
  doWylacznie: string;
  wymagaDzialania: boolean;
  /** Filtry szczegółowe — już sprawdzone (`czytajFiltry`), `null` = brak filtra. */
  rodzaj: RodzajDokumentuMagazynowego | null;
  zakladId: string | null;
  nr: string | null;
}

export interface StronaDokumentow {
  wiersze: DokumentObiegu[];
  /** Przyszedł 51. wiersz — jest strona starszych. */
  jestNastepna: boolean;
}

/**
 * Jedna strona listy: okres po dniu operacji (półotwarty: `gte od`, `lt` dnia PO
 * ostatnim), najnowsze na górze, porządek całkowity (`id` na końcu, bo ani data,
 * ani numer kolejki nie są unikalne). Prosi o 51 wierszy: 50 na stronę + jeden,
 * który mówi, czy jest następna.
 */
export async function listaDokumentowObiegu(
  organizationId: string,
  filtry: FiltryZapytaniaDokumentow,
  strona: number
): Promise<StronaDokumentow> {
  const offset = (Math.max(1, Math.floor(strona)) - 1) * ROZMIAR_STRONY;
  let zapytanie = supabase
    .from('commerce_documents')
    .select(KOLUMNY_DOKUMENTU_OBIEGU.join(', '))
    .eq('organization_id', organizationId)
    .gte('data_operacji', filtry.od)
    .lt('data_operacji', filtry.doWylacznie);
  if (filtry.rodzaj) zapytanie = zapytanie.eq('rodzaj', filtry.rodzaj);
  if (filtry.zakladId) zapytanie = zapytanie.eq('zaklad_id', filtry.zakladId);
  if (filtry.nr) zapytanie = zapytanie.ilike('numer_zrodlowy', `%${ucieknijLike(filtry.nr)}%`);
  if (filtry.wymagaDzialania) zapytanie = zapytanie.or(FILTR_WYMAGA_DZIALANIA);
  const { data, error } = await zapytanie
    .order('data_operacji', { ascending: false })
    .order('data_wystawienia_at', { ascending: false })
    .order('id', { ascending: false })
    .range(offset, offset + ROZMIAR_STRONY);
  if (error || !Array.isArray(data)) {
    throw new ReceptorCoreError(
      0,
      'odczyt_dokumentow',
      `Nie udało się wczytać dokumentów${error ? `: ${error.message}` : ''}`
    );
  }
  const wiersze = data as unknown as DokumentObiegu[];
  return {
    wiersze: wiersze.slice(0, ROZMIAR_STRONY),
    jestNastepna: wiersze.length > ROZMIAR_STRONY,
  };
}

/**
 * Liczba przy chipie „Wymaga działania” — zliczenie `head` w tym samym okresie,
 * z tymi samymi filtrami rodzaju, zakładu i numeru i z tym samym filtrem grupy
 * co lista. Brak liczby w odpowiedzi to błąd, nie zero: „0” mówi człowiekowi,
 * że nic nie czeka.
 */
export async function liczbaWymagaDzialania(
  organizationId: string,
  filtry: Omit<FiltryZapytaniaDokumentow, 'wymagaDzialania'>
): Promise<number> {
  let zapytanie = supabase
    .from('commerce_documents')
    .select('id', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .gte('data_operacji', filtry.od)
    .lt('data_operacji', filtry.doWylacznie);
  if (filtry.rodzaj) zapytanie = zapytanie.eq('rodzaj', filtry.rodzaj);
  if (filtry.zakladId) zapytanie = zapytanie.eq('zaklad_id', filtry.zakladId);
  if (filtry.nr) zapytanie = zapytanie.ilike('numer_zrodlowy', `%${ucieknijLike(filtry.nr)}%`);
  const { count, error } = await zapytanie.or(FILTR_WYMAGA_DZIALANIA);
  if (error || typeof count !== 'number') {
    throw new ReceptorCoreError(
      0,
      'odczyt_dokumentow',
      `Nie udało się policzyć dokumentów wymagających działania${error ? `: ${error.message}` : ''}`
    );
  }
  return count;
}

/**
 * Najnowszy dokument organizacji w CAŁYM rejestrze — bez okresu i bez filtrów,
 * w porządku listy. Rozdziela „pusto w okresie” od „pusto w ogóle”; indeks
 * `(organization_id, data_operacji DESC)` daje pierwszy wiersz bez przeglądania
 * reszty. `null` = rejestr organizacji jest pusty; błąd to wyjątek, nie `null`.
 */
export async function najnowszyDokument(organizationId: string): Promise<NajnowszyDokument | null> {
  const { data, error } = await supabase
    .from('commerce_documents')
    .select('numer_zrodlowy, rodzaj, data_operacji')
    .eq('organization_id', organizationId)
    .order('data_operacji', { ascending: false })
    .order('data_wystawienia_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(1);
  if (error || !Array.isArray(data)) {
    throw new ReceptorCoreError(
      0,
      'odczyt_dokumentow',
      `Nie udało się sprawdzić najnowszego dokumentu${error ? `: ${error.message}` : ''}`
    );
  }
  return (data[0] as unknown as NajnowszyDokument | undefined) ?? null;
}
